/**
 * First-party website analytics — lightweight, dependency-free capture client.
 *
 * Buffers pageview + click events and flushes them in batches to the public
 * ingest endpoint (`/api/public/etf-website/events`) via `navigator.sendBeacon`
 * (with a `fetch({ keepalive })` fallback). No third-party SDK, no cookies — an
 * anonymous visitor id lives in localStorage and a rolling session id (30-min
 * idle window) in localStorage too, so reporting can stitch a journey together.
 *
 * Privacy: honors Do Not Track; stores no PII client-side; the server hashes the
 * IP. Bots are dropped server-side.
 */

const ENDPOINT = "/api/public/etf-website/events";

const VISITOR_KEY = "cf_vid";
const SESSION_KEY = "cf_sid";
const SESSION_TS_KEY = "cf_sid_ts";
const UTM_KEY = "cf_utm";

const SESSION_IDLE_MS = 30 * 60 * 1000; // 30-min rolling idle window
const FLUSH_DEBOUNCE_MS = 2000;
const MAX_BUFFER = 20;

export type Intent = "high" | "medium" | "low";

export interface TrackOptions {
  intent?: Intent;
  path?: string;
  fundTicker?: string;
  href?: string;
  text?: string;
  props?: Record<string, unknown>;
}

interface QueuedEvent {
  name: string;
  intent: Intent;
  path?: string;
  fund_ticker?: string;
  href?: string;
  text?: string;
  props?: Record<string, unknown>;
  ts: number;
}

interface Attribution {
  referrer?: string;
  landing_path?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

let buffer: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let started = false;

function hasWindow(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/** Respect Do Not Track / Global Privacy Control. */
function isEnabled(): boolean {
  if (!hasWindow()) return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  if (nav.doNotTrack === "1" || nav.globalPrivacyControl === true) return false;
  return true;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore (private mode / quota)
  }
}

function getVisitorId(): string {
  let id = lsGet(VISITOR_KEY);
  if (!id) {
    id = uuid();
    lsSet(VISITOR_KEY, id);
  }
  return id;
}

/** Session id with a 30-min rolling idle window (persisted so it survives reloads). */
function getSessionId(): string {
  const now = Date.now();
  const last = Number(lsGet(SESSION_TS_KEY) ?? 0);
  let id = lsGet(SESSION_KEY);
  if (!id || !last || now - last > SESSION_IDLE_MS) {
    id = uuid();
    lsSet(SESSION_KEY, id);
  }
  lsSet(SESSION_TS_KEY, String(now));
  return id;
}

/** Capture UTM params (once per session) + referrer for attribution. */
function captureAttribution(): void {
  if (!hasWindow()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const k of ["source", "medium", "campaign", "term", "content"]) {
      const v = params.get(`utm_${k}`);
      if (v) utm[`utm_${k}`] = v.slice(0, 255);
    }
    const existing = sessionStorage.getItem(UTM_KEY);
    if (Object.keys(utm).length > 0 && !existing) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  } catch {
    // ignore
  }
}

function getAttribution(): Attribution {
  const attr: Attribution = {};
  if (!hasWindow()) return attr;
  // External referrer only (don't record same-site navigations as referrers).
  try {
    if (document.referrer) {
      const ref = new URL(document.referrer);
      if (ref.hostname !== window.location.hostname) {
        attr.referrer = document.referrer.slice(0, 2048);
      }
    }
  } catch {
    // ignore
  }
  attr.landing_path = window.location.pathname;
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    if (raw) Object.assign(attr, JSON.parse(raw) as Record<string, string>);
  } catch {
    // ignore
  }
  return attr;
}

function sendBatch(events: QueuedEvent[]): void {
  if (events.length === 0) return;
  const payload = JSON.stringify({
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    events,
    context: getAttribution(),
  });

  // sendBeacon survives page unload; fall back to keepalive fetch.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
  } catch {
    // fall through to fetch
  }
  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // best-effort; analytics must never throw into the app
  });
}

export function flush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  sendBatch(batch);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
}

/** Queue an event. High-intent events flush immediately so a quick exit
 *  (e.g. an outbound broker click) still records the conversion. */
export function track(name: string, opts: TrackOptions = {}): void {
  if (!isEnabled()) return;
  const intent = opts.intent ?? "low";
  buffer.push({
    name,
    intent,
    path: opts.path ?? (hasWindow() ? window.location.pathname : undefined),
    fund_ticker: opts.fundTicker,
    href: opts.href,
    text: opts.text,
    props: opts.props,
    ts: Date.now(),
  });
  // Keep the session id fresh on every interaction.
  getSessionId();
  if (intent === "high" || buffer.length >= MAX_BUFFER) {
    flush();
  } else {
    scheduleFlush();
  }
}

export function trackPageview(path?: string): void {
  captureAttribution();
  track("pageview", { intent: "low", path });
}

// ── Click classification ──────────────────────────────────────────────────────

function classifyClick(target: HTMLElement): (TrackOptions & { name: string }) | null {
  // 1. Explicit, attribute-driven events win (robust against markup changes).
  const tagged = target.closest<HTMLElement>("[data-analytics-event]");
  if (tagged) {
    const intent = (tagged.dataset.analyticsIntent as Intent) || "low";
    return {
      name: tagged.dataset.analyticsEvent || "click",
      intent,
      fundTicker: tagged.dataset.analyticsFund,
      href: (tagged as HTMLAnchorElement).href || undefined,
      text: (tagged.textContent || "").trim().slice(0, 100) || undefined,
    };
  }

  // 2. Fallback heuristics for plain anchors.
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (!anchor) return null;

  const href = anchor.getAttribute("href") || "";
  if (!href || href.startsWith("javascript:")) return null;

  const text = (anchor.textContent || "").trim().slice(0, 100) || undefined;

  // Outbound broker links live in the "where to buy" section — highest intent.
  if (anchor.closest("#sec-brokerages")) {
    return { name: "broker_click", intent: "high", href: anchor.href, text };
  }

  let external = false;
  try {
    external = new URL(anchor.href, window.location.origin).hostname !== window.location.hostname;
  } catch {
    external = false;
  }
  return {
    name: external ? "outbound_click" : "link_click",
    intent: "low",
    href: anchor.href,
    text,
  };
}

function onClick(e: MouseEvent): void {
  const target = e.target as HTMLElement | null;
  if (!target?.closest) return;
  const result = classifyClick(target);
  if (!result) return;
  const { name, ...opts } = result;
  track(name, opts);
}

/**
 * Initialize global capture: the document-level click listener (capture phase,
 * mirroring LeavingSiteModal) + unload flushers. Idempotent — safe to call once.
 */
export function initAnalytics(): void {
  if (started || !isEnabled()) return;
  started = true;

  getVisitorId();
  getSessionId();
  captureAttribution();

  document.addEventListener("click", onClick, true);

  // Flush on tab hide / unload so in-flight events aren't lost.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}
